/**
 * PageLoader — INTALK branded in-page loader for API/data loading states.
 * Shows the INTALK logo (wordmark + tag/soundwave icon), animated SMS/phone
 * icons, and a progress bar. Theme is built around INTALK's blue palette.
 *
 * Usage:
 *   if (loading) return <PageLoader />;
 *   if (loading) return <PageLoader height="40vh" />;
 *   if (loading) return <PageLoader height="30vh" compact />;
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

// INTALK brand blues
const BLUE_DEEP = '#1273BC';
const BLUE = '#1C9AD6';
const BLUE_BRIGHT = '#29ABE2';

/**
 * InTalkLogo — inline SVG recreation of the INTALK logo:
 * lowercase "Intalk" wordmark + a tag/label icon with an animated
 * soundwave (equalizer) that doubles as an SMS sending indicator.
 */
const InTalkLogo: React.FC<{ size: number }> = ({ size }) => (
  <Box
    component="svg"
    viewBox="0 0 340 110"
    sx={{
      width: size,
      height: 'auto',
      display: 'block',
      '& .il-bar': {
        transformOrigin: 'center',
        animation: 'ilBars 1s ease-in-out infinite',
      },
      '& .il-bar2': { animationDelay: '0.15s' },
      '& .il-bar3': { animationDelay: '0.3s' },
      '@keyframes ilBars': {
        '0%, 100%': { transform: 'scaleY(0.55)' },
        '50%': { transform: 'scaleY(1)' },
      },
    }}
  >
    <defs>
      <linearGradient id="ilWord" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={BLUE_DEEP} />
        <stop offset="1" stopColor={BLUE_BRIGHT} />
      </linearGradient>
      <linearGradient id="ilTag" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={BLUE} />
        <stop offset="1" stopColor={BLUE_BRIGHT} />
      </linearGradient>
    </defs>

    {/* Wordmark "Intalk" */}
    <text
      x="0"
      y="80"
      fill="url(#ilWord)"
      style={{
        fontFamily:
          "'Poppins','Quicksand','Segoe UI',system-ui,sans-serif",
        fontWeight: 700,
        fontSize: 92,
        letterSpacing: '-3px',
      }}
    >
      Intalk
    </text>

    {/* Tag / label icon with soundwave */}
    <g transform="translate(244, 14)">
      <path
        d="M6 0 H56 a14 14 0 0 1 11 5.5 L92 38 a8 8 0 0 1 0 9.5 L67 80.5 a14 14 0 0 1 -11 5.5 H6 a6 6 0 0 1 -6 -6 V6 a6 6 0 0 1 6 -6 Z"
        fill="url(#ilTag)"
      />
      {/* hole */}
      <circle cx="18" cy="18" r="5.5" fill="#fff" />
      {/* soundwave bars */}
      <rect className="il-bar il-bar1" x="30" y="30" width="8" height="26" rx="4" fill="#fff" />
      <rect className="il-bar il-bar2" x="44" y="22" width="8" height="42" rx="4" fill="#fff" />
      <rect className="il-bar il-bar3" x="58" y="34" width="8" height="18" rx="4" fill="#fff" />
    </g>
  </Box>
);

interface PageLoaderProps {
  height?: string;
  message?: string;
  compact?: boolean;
}

const PageLoader: React.FC<PageLoaderProps> = ({
  height = "60vh",
  message = "Connecting your conversations...",
  compact = false,
}) => {
  const logoSize = compact ? 150 : 210;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height={height}
      sx={{ userSelect: "none" }}
    >
      {/* Logo with float animation */}
      <Box sx={{
        position: "relative", mb: compact ? 1.5 : 2.5,
        animation: "plFloat 2.4s ease-in-out infinite",
        "@keyframes plFloat": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-6px)" },
        },
      }}>
        {/* Glow */}
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: logoSize * 0.7, height: logoSize * 0.7, borderRadius: "50%",
          background: `radial-gradient(circle, ${BLUE_BRIGHT}26 0%, transparent 70%)`,
          animation: "plGlow 2.4s ease-in-out infinite",
          "@keyframes plGlow": {
            "0%, 100%": { opacity: 0.3, transform: "translate(-50%, -50%) scale(1)" },
            "50%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.3)" },
          },
        }} />
        <InTalkLogo size={logoSize} />
      </Box>

      {/* SMS / phone icons row */}
      {!compact && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.5, mb: 2,
          animation: "plFadeUp 0.6s ease-out",
          "@keyframes plFadeUp": {
            "0%": { opacity: 0, transform: "translateY(6px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}>
          {[
            { emoji: "📱", delay: "0s" },
            { emoji: "💬", delay: "0.15s" },
            { emoji: "✉️", delay: "0.3s" },
            { emoji: "📲", delay: "0.45s" },
            { emoji: "🔔", delay: "0.6s" },
          ].map((v, i) => (
            <Box key={i} sx={{
              fontSize: compact ? 16 : 20,
              opacity: 0,
              animation: "plMsgPop 0.4s ease-out forwards, plMsgBounce 2s ease-in-out infinite",
              animationDelay: `${v.delay}, ${parseFloat(v.delay) + 0.8}s`,
              "@keyframes plMsgPop": {
                "0%": { opacity: 0, transform: "scale(0.3)" },
                "70%": { transform: "scale(1.15)" },
                "100%": { opacity: 1, transform: "scale(1)" },
              },
              "@keyframes plMsgBounce": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-3px)" },
              },
            }}>
              {v.emoji}
            </Box>
          ))}
        </Box>
      )}

      {/* Progress bar */}
      <Box sx={{ width: compact ? 90 : 130, height: 3, borderRadius: 2, bgcolor: "#e2e8f0", overflow: "hidden", mb: 1.5 }}>
        <Box sx={{
          width: "35%", height: "100%", borderRadius: 2,
          background: `linear-gradient(90deg, ${BLUE_DEEP}, ${BLUE_BRIGHT}, ${BLUE_DEEP})`,
          backgroundSize: "200% 100%",
          animation: "plSlide 1.4s ease-in-out infinite",
          "@keyframes plSlide": {
            "0%": { transform: "translateX(-100%)" },
            "100%": { transform: "translateX(350%)" },
          },
        }} />
      </Box>

      {/* Message with icons */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.75, opacity: 0.4,
        animation: "plMsgIn 0.8s ease-out 0.3s both",
        "@keyframes plMsgIn": {
          "0%": { opacity: 0 },
          "100%": { opacity: 0.4 },
        },
      }}>
        {/* phone icon */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.36 11.36 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" fill="#94a3b8"/>
        </svg>
        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 500, letterSpacing: "0.03em" }}>
          {message}
        </Typography>
        {/* chat bubble icon */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7V9zm6 4H7v-2h6v2zm4-6H7V5h10v2z" fill="#94a3b8"/>
        </svg>
      </Box>
    </Box>
  );
};

export { PageLoader };
export default PageLoader;
